import { CardElement } from '@stripe/react-stripe-js'
import {
  PaymentMethod,
  PaymentRequest,
  Stripe,
  StripeElements,
  StripeError
} from '@stripe/stripe-js'
import { TFunction } from 'i18next'
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { action, computed, observable } from 'mobx'
import { IStores } from '.'
import { PlaceType } from '../components/UFOMap/components/types'
import { sendBookingAnalyticEvent } from '../services/analyticsService'
import { formatApiError, getFromApi } from '../services/apiService'
import configurations from '../utils/config/configurations'
import { IUser } from './RegisterTypes'
import { IBooking, IPaymentForm } from './bookingTypes'

export interface IValidateMobilePhoneNumberResult {
  valid: boolean
  number: string
  local_format: string
  international_format: string
  country_prefix: string
  country_code: string
  country_name: string
  location: string
  carrier: string
  line_type: string
}

export type LoyaltyProgram = {
  reference: string
  name: string
  marketingLabel: string
  dataSharingLabel: string
}

export enum PurchaseOrderState {
  NONE = 'none',
  MANDATORY = 'mandatory',
  OPTIONAL = 'optional',
}

export type IPurchaseOrderState = PurchaseOrderState | null

export type PaymentDetails = {
  paymentPublicApi: string | null
  paymentAccountId: string | null
  loyaltyPrograms: LoyaltyProgram[]
  purchaseOrderState: IPurchaseOrderState
}

const build_VALIDATEVOUCHERCODE_URL = (booking: IBooking, code: string) =>
  `/v5/reserve/validation/${booking.product.reference}/${booking.location.reference}/${booking.vehicleModel.reference}/${booking.schedule.startAt}/${booking.schedule.endAt}/${code}`
const build_GET_PAYMENT_OPTIONS_URL = (booking: IBooking) =>
  `/v5/reserve/userPaymentOptions/${booking.location.reference}`

class PaymentStore {
  store: IStores

  @observable loading = false

  @observable paymentFormValues: (user: IUser | null) => IPaymentForm = (user: IUser | null) => ({
    driver: {
      phoneNumber: user?.phoneNumber ?? '',
      purchaseOrder: user?.purchaseOrder,
      purchaseOrderState: user?.purchaseOrderState,
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      emailConsent: false,
      notificationsConsent: false,
      billingAddress: user?.billingAddress ?? '',
      billingCompanyName: user?.billingCompanyName ?? '',
      billingCompanyVatNumber: user?.billingCompanyVatNumber ?? '',
      birthDate: user?.birthDate ?? '',
      driverLicenseNumber: user?.driverLicenseNumber ?? '',
      nationalInsuranceNumber: user?.nationalInsuranceNumber ?? '',
      vehicleRegistrationNumbers: user?.vehicleRegistrationNumbers ?? ''
    },
    partner: { code: '' },
    voucherCode: ''
  })

  @observable skipPaymentMethod = false
  @observable paymentMethod: PaymentMethod | null = null
  @observable billingAddress: PlaceType | null = null
  @observable purchaseOrder: string | null = null
  @observable purchaseOrderState: IPurchaseOrderState | null = null
  @observable termValidated = false
  @observable loyaltyProgramValidated = true
  @observable securityDepositValidated = configurations.UFO_APP_PLATFORM !== 'pritchards'
  @observable stripe: Stripe | null = null
  @observable stripeElements: StripeElements | null = null
  @observable paymentRequestPending = false
  @observable paymentRequest: PaymentRequest | null = null
  @observable paymentErrors: StripeError | null = null
  @observable isVoucherCodeValid = true
  @observable lastVoucherCheckDate = new Date()

  constructor(store: IStores) {
    this.store = store
  }

  @action setSkipPaymentMethod = (value: boolean): void => {
    this.skipPaymentMethod = value
  }

  @action setPaymentFormValues = (values: any): void => {
    this.paymentFormValues = values
  }

  @action setPurchaseOrder = (value: string): void => {
    this.purchaseOrder = value
  }

  @action setTermValidated = (values: boolean): void => {
    this.termValidated = values
  }

  @action setLoyaltyProgramValidated = (values: boolean): void => {
    this.loyaltyProgramValidated = values
  }

  @action setSecurityDepositValidated = (values: boolean): void => {
    this.securityDepositValidated = values
  }

  @action setStripeElements = (stripeElements: StripeElements): void => {
    this.stripeElements = stripeElements
  }

  @action setStripe = (stripe: Stripe): void => {
    this.loading = true
    this.stripe = stripe
    this.loading = false
  }

  @action createPaymentMethod = async () => {
    this.loading = true

    if (!this.stripe || !this.stripeElements) return
    const cardElement = this.stripeElements.getElement(CardElement)

    if (!cardElement) {
      console.debug('cardElement', cardElement)

      return
    }

    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      type: 'card',
      card: cardElement
    })

    this.paymentMethod = paymentMethod || null
    this.paymentErrors = error || null

    this.loading = false
  }

  @computed get formValues(): IPaymentForm {
    return this.paymentFormValues(this.store.registerStore.user)
  }

  @action validatePhoneNumber = async (
    t: TFunction,
    phoneNumber: string
  ): Promise<string | boolean> => {
    this.loading = true

    const _phoneNumber = parsePhoneNumberFromString(phoneNumber)
    this.loading = false

    if (_phoneNumber?.isPossible() === false) {
      return t('bookingPay:mobilePhoneNumberNotValid') as string
    }

    return true
  }

  @action validateVoucherCode = async (
    t: TFunction,
    code: string
  ): Promise<string | boolean> => {
    if (!code || code === '') {
      return true
    }

    this.loading = true

    if (!this.store.bookingStore.selectedBooking) {
      this.loading = false
      this.isVoucherCodeValid = false

      return false
    }

    const response = await getFromApi(
      build_VALIDATEVOUCHERCODE_URL(
        this.store.bookingStore.selectedBooking,
        code.toUpperCase()
      ),
      {},
      true,
      false
    )

    const isValid = !!(response && response.status === 200)
    let validationMessage = t('bookingPay:voucherCodeNotValid')

    if (!isValid) {
      validationMessage = formatApiError(response)?.message
    }
    this.isVoucherCodeValid = isValid
    this.lastVoucherCheckDate = new Date()

    if (isValid) {
      sendBookingAnalyticEvent('book_step_3_add_voucher',
        this.store.locationsStore.selectedCity,
        this.store.locationsStore.selectedLocation,
        this.store.carsStore.proposedBookings?.bookings,
        this.store.bookingStore.selectedBookingIndex,
        this.store.bookingStore.selectedBooking
      )
    }

    this.loading = false

    return isValid || validationMessage
  }

  @action getPaymentDetails = async (): Promise<PaymentDetails | null> => {
    this.loading = true

    if (!this.store.bookingStore.selectedBooking) {
      this.loading = false

      return null
    }

    const response = await getFromApi(
      build_GET_PAYMENT_OPTIONS_URL(this.store.bookingStore.selectedBooking),
      {},
      true,
      false
    )

    const isValid = !!(response && response.status === 200)

    if (!isValid) {
      this.loading = false

      return null
    }

    sendBookingAnalyticEvent('begin_checkout',
      this.store.locationsStore.selectedCity,
      this.store.locationsStore.selectedLocation,
      this.store.carsStore.proposedBookings?.bookings,
      this.store.bookingStore.selectedBookingIndex,
      this.store.bookingStore.selectedBooking
    )

    this.loading = false
    this.purchaseOrderState = response.data.purchaseOrderState

    return {
      paymentPublicApi: response.data.paymentPublicApi,
      paymentAccountId: response.data.paymentAccountId,
      loyaltyPrograms: response.data.loyaltyPrograms,
      purchaseOrderState: response.data.purchaseOrderState
    }
  }

  @action reset = () => {
    this.loading = true
    this.loading = false
  }
}

export default PaymentStore
